import { Point as PeaksPoint, PointOptions, Segment as PeaksSegment, SegmentOptions } from "peaks.js"
import { RequestInit as RequestInitSuper } from "node-fetch";
import { ITrack } from "@/interfaces/ITrack"
import { IPlaylist } from "@/interfaces/IPlaylist"
import { ModalTypes } from "@/enum/modalTypes"
import { ContextMenuTypes } from "@/enum/contextMenuTypes"
import { SnackbarEventTypes } from "@/enum/snackbarEventTypes.ts";
import { IUser } from "@/interfaces/IUser.ts";
import { URLSearchParamKeys } from "@/enum/URLSearchParamKeys.ts";
import { ExplorerItemType } from "@/enum/explorerItemType.ts";
import { OverridableStringUnion } from "@mui/types";
import { ButtonPropsSizeOverrides } from "@mui/material";
import {
  PeaksInstanceType, WAVEFORM_OVERVIEW_TYPE_NAME, WAVEFORM_ZOOMVIEW_TYPE_NAME
} from "@/features/AudioWorkstation/utils/audio-editor-utils.ts";
import React, { RefObject } from "react";
import { Descendant } from "slate";
import { DOMEditor } from "slate-dom/dist/plugin/dom-editor";
import { User } from "next-auth";
import { AudioAdapter } from "@/features/AudioWorkstation/adapters/AudioAdapter.ts";

export const WAVEFORM_CONTAINER_CLASSNAME = 'waveform-container';
export const NO_SELECTO_CLASSNAME = 'no-selecto'
export const AMOUNT_OF_TRACKS_LIMIT = 100
export const UPDATE_PLAYLIST_STATUS = "UPDATE_PLAYLIST_STATUS"
export const LOADING_INITIAL_TRACKS = "LOADING_INITIAL_TRACKS"
export const LOADING_NEXT_PAGE_OF_TRACKS = "LOADING_NEXT_PAGE_OF_TRACKS"
export const READY = "READY"
export type ReactStateReturn<T> = [value: T, updateValue: React.Dispatch<React.SetStateAction<T>>];

export interface PushSubscriptionData {
  endpoint: string;
  expirationTime: number | null;
  keys: {
    p256dh: string;
    auth: string;
  };
}

export interface UpdatePushSubscriptionParams {
  userId: string;
  subscriptionData: PushSubscriptionData;
  browserInfo: string;
  deviceInfo: string;
}

export type NextFetchRequestConfig = RequestInit & {
  cache?: RequestInit['cache'] | 'force-cache';
  next?: {
    revalidate?: number | false;
    tags?: string[];
  };
};

export interface ApiRequest {
  jwt: string
}

export type KeyboardEventHandler = (event: React.KeyboardEvent<Element>) => void;

export interface KeyboardEventHandlerOptions {
  forceEnable?: boolean;
  forceDisable?: boolean;
  ignoreWriting?: boolean;
  ignoreModals?: boolean;
  allowedModalTypes?: ModalTypes[];
}

export enum NotificationTypes {
  USER_SEGMENT_MENTION_NOTIFICATION = "USER_SEGMENT_MENTION_NOTIFICATION",
  USER_POINT_MENTION_NOTIFICATION = "USER_POINT_MENTION_NOTIFICATION",
  SHARED_TRACK_NOTIFICATION = "SHARED_TRACK_NOTIFICATION",
}

export interface NotificationType {
  type: NotificationTypes;
  id: string;
  isRead: boolean;
  message: string;
  createdAt: string;
  createdByUser: IUser;
  createdForUser?: IUser;
  explorerItem?: ExplorerItem;
  point?: Point;
  segment?: Segment;
  uniqueTabId: string;
}

export type Conversation = {
  id?: string,
  body: string, // Descendant[],
  createdByEmailAddress: string
  createdByUser?: IUser
  createdById?: string
  createdAt?: string | null
  pointId?: string | null
  segmentId?: string | null
}

export interface ConnectionInfo {
  id: string;
  type: ConnectionType;
  name: string;
  emailAddress?: string;
  avatar?: string;
  lastActive?: string;
  location?: string;
}

export enum ConnectionType {
  USER = 'user',
  BAND = 'band',
}

export enum SlateElementTypes {
  PARAGRAPH = 'paragraph',
  USER_MENTION = 'user_mention',
  BAND_MENTION = 'band_mention',
}

export type SlateEditorContent = {
  id: string,
  elements: Descendant[],
}

export type SlateBandMentionElement = {
  type: SlateElementTypes.BAND_MENTION;
  mention: BandConnection;
  children: Descendant[];
};

export type SlateUserMentionElement = {
  type: SlateElementTypes.USER_MENTION;
  mention: UserConnection;
  children: Descendant[];
};

export type SlateParagraphElement = {
  type: SlateElementTypes.PARAGRAPH;
  children: Descendant[];
};

export type SlateEditorElement = SlateUserMentionElement | SlateBandMentionElement | SlateParagraphElement;

export interface MentionEditorRef {
  getEditor: () => DOMEditor;
  clearContent: () => void;
  triggerOpenConnectionsList: () => void;
  getContent: () => Descendant[];
}

interface SlateTextNode {
  text: string;
}

type SlateElement = SlateParagraphElement | SlateUserMentionElement | SlateBandMentionElement;
type SlateNode = SlateElement | SlateTextNode;

export function getAllNotifiedEmails(conversations: Conversation[]) {
  const allEmails: string[] = [];

  conversations.forEach(conversation => {
    if (conversation.body) {
      try {
        const bodyContent = JSON.parse(conversation.body);
        const conversationEmails = extractSlateEditorUserEmails(bodyContent);
        allEmails.push(...conversationEmails);
      } catch (error) {
        console.error(`Fout bij het parsen van body voor conversation ID ${conversation.id}:`, error);
      }
    }
  });

  return [...new Set(allEmails as string[])];
}

export const extractSlateEditorUserEmails = (data: SlateNode[]): string[] => {
  const emails: string[] = [];

  const traverse = (node: SlateNode): void => {
    // Type guard voor element nodes
    if ('type' in node) {
      if (node.type === SlateElementTypes.USER_MENTION &&
        'mention' in node &&
        node.mention &&
        node.mention.emailAddress) {
        emails.push(node.mention.emailAddress);
      }
    }

    if ('children' in node && Array.isArray(node.children)) {
      node.children.forEach(child => traverse(child));
    }
  };

  data.forEach(node => traverse(node));

  return emails as string[];
};

export interface ViewMode {
  width: number;
}

export interface UserConnection {
  id: string;
  connectionId: string;
  name: string;
  emailAddress: string;
  type: ConnectionType.USER
}

export interface BandConnection {
  id: string;
  connectionId: string;
  name: string;
  type: ConnectionType.BAND
}

export type ShareLinkDataType = {
  sharedByEmailAddress: string,
  sharedWithEmailAddress: string,
  redirectPath: string
  isNewAccount?: string,
  explorerItemAccessType?: string,
  message?: string,
  explorerItemId?: string,
  explorerItemType?: string,
} | null

export type UserAccountActivationLinkDataType = {
  emailAddress: string,
  password: string,
  isNewAccountByRegistration?: string,
} | null

enum MediaState {
  PAUSED = "PAUSED",
  STOPPED = "STOPPED",
  PLAYING = "PLAYING",
}

enum RepeatMode {
  NORMAL = "NORMAL",
  REPEAT_ALL = "REPEAT_ALL",
  REPEAT_ONE = "REPEAT_ONE",
}

/**
 * Custom native events for interface
 * @enum {string}
 */
const CustomNativeEventTypes = {
  PLAY: "PLAY",
  PLAY_LATER: "PLAY_LATER",
  PLAY_NEXT: "PLAY_NEXT",
}

const EventTypes = {
  // PLAY_TRACK: "PLAY_TRACK",
  UPDATE_AUDIO_VOLUME_SLIDER: "UPDATE_AUDIO_VOLUME_SLIDER",
  TRIGGER_RELOAD_NOTIFICATIONS_MENU: "TRIGGER_RELOAD_NOTIFICATIONS_MENU",
  TRIGGER_RELOAD_SEGMENT_CONVERSATIONS: "TRIGGER_RELOAD_SEGMENT_CONVERSATIONS",
  TRIGGER_RELOAD_POINT_CONVERSATIONS: "TRIGGER_RELOAD_POINT_CONVERSATIONS",
  TOGGLE_AUDIO_TRACKS_DRAWER: "OPEN_AUDIO_TRACKS_DRAWER",
  OPEN_SNACKBAR: "OPEN_SNACKBAR",
  OPEN_CONFIRM_DIALOG: "OPEN_CONFIRM_DIALOG",
  TOGGLE_EDITOR: "TOGGLE_EDITOR",
  CLEAR_SEGMENT_SLATE_EDITOR: "CLEAR_SEGMENT_SLATE_EDITOR",
  CLEAR_POINT_SLATE_EDITOR: "CLEAR_POINT_SLATE_EDITOR",
  UPDATE_SLATE_AVAILABLE_MENTIONS: "UPDATE_SLATE_AVAILABLE_MENTIONS",
  FOCUS_SLATE_REPLY_FIELD: "FOCUS_SLATE_REPLY_FIELD",
  SHARED_TRACK_ADD_NOTIFICATION: "SHARED_TRACK_ADD_NOTIFICATION",
  POINT_USER_MENTION_ADD_NOTIFICATION: "POINT_USER_MENTION_ADD_NOTIFICATION",
  SEGMENT_USER_MENTION_ADD_NOTIFICATION: "SEGMENT_USER_MENTION_ADD_NOTIFICATION",
  POINT_CONVERSATION_UPDATED: "POINT_CONVERSATION_UPDATED",
  POINT_CONVERSATION_ITEM_DELETED: "POINT_CONVERSATION_ITEM_DELETED",
  SEGMENT_CONVERSATION_UPDATED: "SEGMENT_CONVERSATION_UPDATED",
  SEGMENT_CONVERSATION_ITEM_DELETED: "SEGMENT_CONVERSATION_ITEM_DELETED",
  POINT_MOUSE_ENTER_SHOW_MESSAGE: "POINT_MOUSE_ENTER_SHOW_MESSAGE",
  POINT_MOUSE_LEAVE_HIDE_MESSAGE: "POINT_MOUSE_LEAVE_HIDE_MESSAGE",
  CUE_POINT_MESSAGE: "CUE_POINT_MESSAGE",
  RESET_UPLOAD_QUEUE: "RESET_UPPY_UPLOAD_QUEUE",
  SEARCH: "SEARCH",
  FOCUS_SEARCH: "FOCUS_SEARCH",
  SELECT_ROW: "SELECT_ROW",
  SEEK_AUDIO: "SEEK_AUDIO",
  ZOOM_CHANGED: "ZOOM_CHANGED",
  ZOOM_FULL: "ZOOM_FULL",
  ZOOM_IN: "ZOOM_IN",
  ZOOM_OUT: "ZOOM_OUT",
  TRACK_SOURCE_CHANGED: "TRACK_SOURCE_CHANGED",
  WAVEFORM_CREATED: "WAVEFORM_CREATED",
  WAVEFORM_SOURCE_CHANGED: "WAVEFORM_SOURCE_CHANGED",
  OPENED_PLAYLIST_SCROLL_TO_TRACK: "OPENED_PLAYLIST_SCROLL_TO_TRACK",
  GOOGLE_DRIVE_SIGN_IN: "GOOGLE_DRIVE_SIGN_IN",
  GOOGLE_DRIVE_DOWNLOAD_TO_PLAYLIST: "GOOGLE_DRIVE_DOWNLOAD_TO_PLAYLIST",
  SHOW_PROGRESS_BAR_LOADER: "SHOW_PROGRESS_BAR_LOADER",
  SET_DOCUMENT_CURSOR: "SET_DOCUMENT_CURSOR",
  UNSET_DOCUMENT_CURSOR: "UNSET_DOCUMENT_CURSOR",
  ENABLE_SEGMENT_DRAGGING: "ENABLE_SEGMENT_DRAGGING",
  DISABLE_SEGMENT_DRAGGING: "DISABLE_SEGMENT_DRAGGING",
  REFRESH_ROUTE: "REFRESH_ROUTE",
  NAVIGATE_TO_ROUTE: "NAVIGATE_TO_ROUTE",
  USER_EVENT_PLAYING_TRACK: "USER_EVENT_PLAYING_TRACK",
  USER_EVENT_SHOW_SNACKBAR: "USER_EVENT_SHOW_SNACKBAR",
  HOVERED_POINT_IN_WAVEFORM: "SET_CURRENTLY_HOVERED_POINT_IN_WAVEFORM",
  INIT_AUDIO_EDITOR_PEAKS: "INIT_AUDIO_EDITOR_PEAKS",
  SET_CURRENTLY_HOVERED_SEGMENT_IN_WAVEFORM: "SET_CURRENTLY_HOVERED_SEGMENT_IN_WAVEFORM",
  CREATE_AUDIO_EDITOR_PEAKS_INSTANCE: "CREATE_AUDIO_EDITOR_PEAKS_INSTANCE",
  CREATE_LIST_PEAKS_INSTANCE: "CREATE_LIST_PEAKS_INSTANCE",
  RECORD_SELECTION: "RECORD_SELECTION",
  AUDIO_SEEKED: "AUDIO_SEEKED",
  EXPLORER_TREE_ITEM_SELECTED: "EXPLORER_TREE_ITEM_SELECTED",
  POINT_CREATED_OPEN_MODAL: "POINT_CREATED",
  POINT_UPDATED: "POINT_UPDATED",
}

export interface CreateListPeaksInstanceParams {
  track: ITrack | null,
  container: RefObject<HTMLDivElement | null>,
  callbackFunction: (error: Error, peaks: PeaksInstanceType) => void,
  themeMode: string
  userId: string;
}

export interface CreateAudioEditorPeaksInstanceParams {
  audioAdapter: AudioAdapter
  track: ITrack;
  zoomviewContainerRef: RefObject<HTMLDivElement | null>;
  overviewContainerRef: RefObject<HTMLDivElement | null>;
  scrollbarContainerRef: RefObject<HTMLDivElement | null>;
  initCallback: (error: Error, peaks: PeaksInstanceType) => void;
  viewMode: ViewMode;
  userId: string;
}

export interface CreateWaveformPlayerPeaksInstanceParams {
  audioAdapter: AudioAdapter;
  track: ITrack;
  overviewContainerRef: RefObject<HTMLDivElement | null>;
  initCallback: (error: Error, peaks: PeaksInstanceType) => void;
  viewMode: ViewMode;
  userId: string;
}

/**
 * Store action types
 * @enum {string}
 */

export { EventTypes, CustomNativeEventTypes, MediaState, RepeatMode }

export interface Route {
  name: string
  params: object
}

export type TracklistTracks = {
  [trackId: string]: ITrack
}

export type Tracklist = {
  [playlistId: string]: TracklistTracks
}

export type ScrollTopSessionStorageType = {
  [playlistId: string]: number
}

export type Playlists = {
  [playlistId: string]: IPlaylist
}

export type SnackbarType = keyof typeof SnackbarEventTypes;
export type CurrentSnackbarType = {
  type: SnackbarType | null
}

export type UrlSearchParamKeysType = Partial<Record<URLSearchParamKeys, unknown>>

export type ModalType = keyof typeof ModalTypes;
export type CurrentModalType = {
  type: ModalTypes | null
  isMaximized?: boolean
  isMinimized?: boolean
  data?: object
}
export type UploadTracksState = {
  isUploading: boolean
  fileName: string | null
}
export type DownloadTracksState = {
  isDownloading: boolean
  fileName: string | null
}
export const UploadTracksStateInitialData: UploadTracksState = {
  isUploading: false,
  fileName: null,
}
export const DownloadTracksStateInitialData: DownloadTracksState = {
  isDownloading: false,
  fileName: null,
}

export const CurrentModalInitialData: CurrentModalType = {
  type: null,
  data: {},
  isMaximized: false,
  isMinimized: false,
}

export type ContextMenuCoordinatesType = {
  x: number,
  y: number,
}

export type ContextMenuType = keyof typeof ContextMenuTypes;
export type ContextDataMenuType = WaveformContextMenuType
  | ExplorerContextMenuType
  | ExplorerTreeItemContextMenuType
  | ExplorerItemContextMenuType
  | TrackContextMenuType
  | WaveformSegmentContextMenuType
  | SegmentRowContextMenuType
  | SegmentTagContextMenuType
  | PointTagContextMenuType
  | TrackTagContextMenuType
  | PointRowContextMenuType
  | PlaylistContextMenuType | null;

export type CurrentContextMenuType = {
  isOpen?: boolean
  type: ContextMenuType | null
  data?: ContextDataMenuType | undefined
}

export const CurrentContextmenuInitialData: CurrentContextMenuType | null = {
  type: null,
  data: null,
  isOpen: false,
}

export interface BaseContextMenuType {
  mouseEvent: React.MouseEvent | React.PointerEvent | PointerEvent | null
  contextMenuRef?: React.RefObject<HTMLElement>
}

export interface WaveformContextMenuType extends BaseContextMenuType {
  mouseEvent: PointerEvent;
}

export type ExplorerContextMenuType = BaseContextMenuType

export interface ExplorerTreeItemContextMenuType extends BaseContextMenuType {
  parentPlaylistId: ExplorerItemId;
}

export interface ExplorerItemContextMenuType extends BaseContextMenuType {
  explorerItem: ExplorerItem;
}

export interface TrackContextMenuType extends BaseContextMenuType {
  explorerItem: ExplorerItem;
}

export interface TrackTagContextMenuType extends BaseContextMenuType {
  trackId: string;
  tag: TagOption;
}

export interface SegmentTagContextMenuType extends BaseContextMenuType {
  segmentId: string;
  trackId: string;
  tag: TagOption;
}

export interface WaveformSegmentContextMenuType extends BaseContextMenuType {
  segment: Segment;
}

export interface PointTagContextMenuType extends BaseContextMenuType {
  pointId: string;
  trackId: string;
  tag: TagOption;
}

export interface SegmentRowContextMenuType extends BaseContextMenuType {
  segment: Segment
}

export interface PointRowContextMenuType extends BaseContextMenuType {
  point: Point
}

export interface PlaylistContextMenuType extends BaseContextMenuType {
  playlistId: string,
  explorerItemId: string
}

export type PeaksViewType = typeof WAVEFORM_ZOOMVIEW_TYPE_NAME | typeof WAVEFORM_OVERVIEW_TYPE_NAME | undefined;

export type Segment = {
  id?: string,
  tags?: SegmentTagsType,
  exportedTracks?: TracklistTracks
  description?: string | undefined,
  userAttributes?: {
    draggingActiveState?: boolean
    segmentColor?: string
  },
  createdAt?: string,
  updatedAt?: string,
} & PeaksSegment

export interface Point extends PeaksPoint {
  tags?: PointTagsType,
  info?: string | undefined
  createdAt?: string
  user?: { avatar: string, emailAddress: string }
}

// @TODO: ask Chris Needham (https://github.com/chrisn) to make an export of this
export interface PointPointerEvent {
  point: Point;
  evt: PointerEvent;
  preventViewEvent: () => void;
}

export interface Points {
  [key: string]: PointOptions & Point
}

export interface Segments {
  [key: string]: SegmentOptions & Segment
}

export type TagOption = {
  created_by_id: string
  email: string
  tag_id?: string
  name?: string
}

export type ProgressLoaderDataType = {
  isLoading: boolean
  progress?: number
  data?: object
}

export const ProgressLoaderInitialData: ProgressLoaderDataType = {
  isLoading: false,
  progress: 0,
  data: {}
}

export type CurrentSnackbarProps = {
  snackbar: CurrentSnackbarData
  onClose: () => void
}

export type CurrentSnackbarData = {
  type: SnackbarEventTypes;
  data: SnackbarDataTypes[SnackbarEventTypes];
  eventCreatedByUser: IUser;
  uniqueTabId: string;
};

export type SnackbarDataTypes = {
  [SnackbarEventTypes.SEGMENT_CONVERSATION_USER_MENTIONED]: {
    message: string,
    segment: Segment,
    segmentId: string,
    track: ITrack,
    trackName: string,
    explorerItem: ExplorerItem,
    explorerItemId: string,
  };
  [SnackbarEventTypes.POINT_CONVERSATION_USER_MENTIONED]: {
    message: string,
    point: Point,
    pointId: string,
    track: ITrack,
    trackName: string,
    explorerItem: ExplorerItem,
    explorerItemId: string,
  };
  [SnackbarEventTypes.USER_EVENT_PLAYING_TRACK]: { track: ITrack };
  [SnackbarEventTypes.USER_EVENT_SHARED_TRACK]: {
    explorerItem: ExplorerItem,
    message: string,
    sharedWithUser: IUser,
    sharedByUser: IUser,
    uniqueTabId: string
  };
  [SnackbarEventTypes.USER_EVENT_SHARED_SEGMENT]: { track: ITrack, segment: Segment, message: string };
}

export type TagPoint = { id: string, track_id: string, name: string }
export type TagPointsType = Array<TagPoint>

/*export type TrackTagsType = {
  [K in TrackTagType]?: Record<string, TagOption>|TagOption[];
}*/

export type ExplorerItemTagsType = TagOption[]
export type SegmentTagsType = TagOption[]
export type PointTagsType = TagOption[]

export type WaveformScrollbar = {
  _scrollboxWidth: number
}

export interface MutateExplorerItems {
  prependItem: (item: ExplorerItem) => void;
  setItems: (item: ExplorerItems) => void;
  prependItems: (item: ExplorerItems) => void;
  updateItem: (item: ExplorerItem) => void;
  updateItems: (item: ExplorerItem[]) => void;
  appendItem: (item: ExplorerItem) => void;
  appendItems: (items: ExplorerItems) => void;
  removeItem: (itemId: string) => void;
  removeItems: (itemIds: string[]) => void;
  removePlaylist: (itemId: string) => void;
  removePlaylists: (itemIds: string[]) => void;
  updateTrackSegment: (itemId: ExplorerItemId, segment: Segment) => void;
  deleteTrackSegment: (itemId: ExplorerItemId, segment: Segment) => void;
  upsertTrackSegment: (itemId: ExplorerItemId, segment: Segment, uniqueTabId: string, isRecording?: boolean) => void;
}

export interface AudioAudioMutateType {
  crop: (item: ExplorerItem, startTime: number, endTime: number) => void
  fadeIn: (item: ExplorerItem, startTime: number, endTime: number) => void
  fadeOut: (item: ExplorerItem, startTime: number, endTime: number) => void
  removeAudio: (item: ExplorerItem, startTime: number, endTime: number) => void
  silenceAudio: (item: ExplorerItem, startTime: number, endTime: number) => void
}

export interface AudioPlayback {
  stop: () => void;
  pause: () => void;
  play: (explorerItem?: ExplorerItem | null) => Promise<void>;
  replay: () => void;
  seek: (time: number) => void;
  togglePlay: (explorerItem?: ExplorerItem | null) => void;
  setSource: (explorerItem: ExplorerItem, force?: boolean) => void;
  playNextTrack: (explorerItem: ExplorerItem) => void;
  playPreviousTrack: (explorerItem: ExplorerItem) => void;
}

export interface DashboardNotificationItemClickCallback {
  notification: NotificationType,
  onClickCallback?: (event: React.MouseEvent<HTMLElement, MouseEvent>, notificationId: string) => void,
}

export interface ExplorerBreadcrumbType {
  id: string;
  name: ExplorerItem['name'];
  isLast?: boolean;
  explorerItemParentId?: string;
}

export type ExplorerBreadcrumbs = ExplorerBreadcrumbType[];

export interface ExplorerItem {
  id: ExplorerItemId;
  liked?: boolean,
  name: string;
  createdById: string;
  type: ExplorerItemType;
  track: ITrack;
  playlistId: string;
  explorerItemParentId: ExplorerItemId;
  sortOrder: number;
  parents: string[];
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string;
  hidden?: boolean;
  isTemporary?: boolean;
  uploadId?: string;
  fileLastModifiedAt?: string;
  fileCreatedAt?: string;
}

export type ValidationError = {
  field: string;
  message: string[];
  code: string;
}

export type ApiErrorResponse = {
  statusCode: number;
  timestamp: string;
  path: string;
  message: string | string[];
  error?: string;
  code?: string;
  validationErrors?: ValidationError[];
  stack?: string;
};

export interface BandWorkspace {
  id: number | null;
  name: string | null;
  slug: string | null;
  createdBy: User | null;
  createdAt?: string | null;
  users?: UserConnection[],
  bands?: BandConnection[],
}

export interface ExplorerItems {
  [itemId: string]: ExplorerItem
}

export interface SearchExplorerItemsRequest {
  searchTerm: string,
}

export interface ExplorerItemsRequest {
  bandWorkspaceId?: number | null,
  type?: ExplorerItemType,
  folderId?: string | null,
  offset?: number,
  limit?: number,
}

export type ExplorerItemId = string/* | null*/

export type ButtonSize = OverridableStringUnion<"small" | "medium" | "large", ButtonPropsSizeOverrides>

interface NextFetchOptions {
  tags?: string[];
  revalidate?: number | string;
}

export interface NextRequestInit extends RequestInitSuper {
  next?: NextFetchOptions;
  cache?: string
}

export interface SSEAudioSeekedEvent {
  originalEvent: Event;
  currentTime: number;
}

export interface QueryParamsContextType {
  queryParams: Record<URLSearchParamKeys, string | null>;
  getQueryParam: (key: URLSearchParamKeys) => string | null;
  setQueryParam: (key: URLSearchParamKeys, value: string) => void;
  setQueryParams: (params: Partial<Record<URLSearchParamKeys, string>>) => void;
  setQueryModalParams: (
    modalKey: URLSearchParamKeys,
    value: string,
    additionalParams?: Partial<Record<URLSearchParamKeys, string>>
  ) => void;
  removeQueryParam: (key: URLSearchParamKeys) => void;
  clearAllQueryParams: () => void;
  replaceAllQueryParams: (params: Partial<Record<URLSearchParamKeys, string>>) => void;
}

export interface SegmentCueEvent {
  segment: Segment;
  time: number;
}

export interface PointCueEvent {
  point: Point;
  time: number;
}

interface RecordingCompletedWithFile {
  file: File;
  explorerItemId: string,
  userId: string,
  startTime: number;
  offsetLeft: number;
  channelCount: number;
  fileSize: number;
  name: string,
  filePath: string,
  mimeType: string,
  duration: number,
  sampleRate: number,
}

export type RecordingCompletedEvent = RecordingCompletedWithFile;

export const AUDIOCONTROL_KEYBOARD_SHORTCUT_ALLOWED_MODAL_TYPES: ModalTypes[] = [
  ModalTypes.TRACK_VIEW,
];
